import React, { useState } from "react";
import { CRMButtonPrimary } from "../CRMButtonPrimary/CRMButtonPrimary";
import { CRMButtonSecondary } from "../CRMButtonSecondary/CRMButtonSecondary";
import { CRMCardOutsidePopupBlank } from "../CRMCardOutsidePopupBlank/CRMCardOutsidePopupBlank";
import { CRMPadding } from "../Simple/CRMPadding/CRMPadding";
import { CRMSpacer } from "../CRMSpacer/CRMSpacer";
import { CRMSpacingColumn } from "../CRMSpacingColumn/CRMSpacingColumn";
import { CRMSpacingRow } from "../CRMSpacingRow/CRMSpacingRow";
import { CRMTitleForCard } from "../CRMTitleForCard/CRMTitleForCard";
import { CRMParagraph } from "../Simple/CRMParagraph/CRMParagraph";
import CRMInputLabelAndErrorWrapComponent from "../CRMInputLabelAndErrorWrapComponent/CRMInputLabelAndErrorWrapComponent";

type TLostReason = "not_selling" | "chose_local_agent" | "chose_auction" | "other";

const LOST_REASONS: ReadonlyArray<TLostReason> = ["not_selling", "chose_local_agent", "chose_auction", "other"];

const REASON_LABELS: Record<TLostReason, string> = {
    not_selling: "Not selling (vendor pulled out)",
    chose_local_agent: "Chose a local agent",
    chose_auction: "Chose auction (third-party)",
    other: "Other (please specify)",
};

export interface ICRMListingLostAndAuctionControlsProps {
    listingId: string;
    currentStatus: string;
    onMarkAgencyLost: (payload: { listing_id: string; lost_reason: TLostReason; lost_reason_notes: string }) => void;
    onReinstate: (payload: { listing_id: string }) => void;
    onSendToAuction: (payload: { listing_id: string; went_to_auction_date: string }) => void;
    onUnsendFromAuction: (payload: { listing_id: string }) => void;
    needsPortalRepublish: boolean;
    onConfirmBackOnPortals: (payload: { listing_id: string }) => void;
}

export const CRMListingLostAndAuctionControls = (props: ICRMListingLostAndAuctionControlsProps): JSX.Element => {
    const [markLostOpen, setMarkLostOpen] = useState(false);
    const [sendToAuctionOpen, setSendToAuctionOpen] = useState(false);
    const [reinstateOpen, setReinstateOpen] = useState(false);
    const [reason, setReason] = useState<TLostReason>("not_selling");
    const [notes, setNotes] = useState("");
    const [auctionDate, setAuctionDate] = useState<string>(new Date().toISOString().slice(0, 10));

    const isTerminal = props.currentStatus === "Lost" || props.currentStatus === "Completed" || props.currentStatus === "At auction";
    const isLost = props.currentStatus === "Lost";
    const isAuction = props.currentStatus === "At auction";

    const submitMarkLost = () => {
        if (reason === "other" && notes.trim() === "") {
            return;
        }
        props.onMarkAgencyLost({
            listing_id: props.listingId,
            lost_reason: reason,
            lost_reason_notes: notes,
        });
        setMarkLostOpen(false);
        setReason("not_selling");
        setNotes("");
    };

    const submitSendToAuction = () => {
        const iso = new Date(`${auctionDate}T09:00:00Z`).toISOString();
        props.onSendToAuction({
            listing_id: props.listingId,
            went_to_auction_date: iso,
        });
        setSendToAuctionOpen(false);
    };

    const submitReinstate = () => {
        props.onReinstate({ listing_id: props.listingId });
        setReinstateOpen(false);
    };

    const onUnsendAuction = () => {
        props.onUnsendFromAuction({ listing_id: props.listingId });
    };

    return (
        <div className="crm-listing-lost-and-auction-controls">
            <CRMSpacingColumn spacing="tiny">
                {props.needsPortalRepublish && (
                    <CRMButtonPrimary
                        label="Confirm back on portals"
                        onClick={() => props.onConfirmBackOnPortals({ listing_id: props.listingId })}
                    />
                )}

                {!isTerminal && (
                    <>
                        <CRMButtonPrimary
                            label="Mark agency lost"
                            onClick={() => setMarkLostOpen(true)}
                        />
                        <CRMButtonPrimary
                            label="Send to auction"
                            onClick={() => setSendToAuctionOpen(true)}
                        />
                    </>
                )}

                {isLost && (
                    <CRMButtonPrimary
                        label="Reinstate listing"
                        onClick={() => setReinstateOpen(true)}
                    />
                )}

                {isAuction && (
                    <CRMButtonPrimary
                        label="Reverse decision (admin only)"
                        onClick={onUnsendAuction}
                    />
                )}
            </CRMSpacingColumn>

            <CRMCardOutsidePopupBlank
                isOpen={markLostOpen}
                onClose={() => setMarkLostOpen(false)}
                context="warning"
            >
                <CRMPadding size="medium">
                    <CRMTitleForCard>Mark agency lost</CRMTitleForCard>
                    <CRMSpacer size="tiny" />
                    <CRMParagraph>
                        The case may still continue for conveyancing — this only ends the agency relationship.
                    </CRMParagraph>
                    <CRMSpacer size="medium" />
                    <div className="crm-listing-lost-and-auction-controls__radio-list">
                        {LOST_REASONS.map((value) => (
                            <label key={value} className="crm-listing-lost-and-auction-controls__radio-row">
                                <CRMSpacingRow spacing="tiny" alignItems="center">
                                    <input
                                        type="radio"
                                        name="lost-reason"
                                        value={value}
                                        checked={reason === value}
                                        onChange={() => setReason(value)}
                                    />
                                    <CRMParagraph>{REASON_LABELS[value]}</CRMParagraph>
                                </CRMSpacingRow>
                            </label>
                        ))}
                    </div>
                    <CRMSpacer size="medium" />
                    <CRMInputLabelAndErrorWrapComponent
                        label={`Notes${reason === "other" ? " (required)" : " (optional)"}`}
                    >
                        <textarea
                            id="lost-reason-notes"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                            className="crm-listing-lost-and-auction-controls__notes-textarea"
                        />
                    </CRMInputLabelAndErrorWrapComponent>
                    <CRMSpacer size="large" />
                    <CRMSpacingRow justifyContent="end">
                        <CRMButtonSecondary
                            label="Cancel"
                            onClick={() => setMarkLostOpen(false)}
                        />
                        <CRMButtonPrimary
                            label="Confirm"
                            disabled={reason === "other" && notes.trim() === ""}
                            onClick={submitMarkLost}
                        />
                    </CRMSpacingRow>
                </CRMPadding>
            </CRMCardOutsidePopupBlank>

            <CRMCardOutsidePopupBlank
                isOpen={sendToAuctionOpen}
                onClose={() => setSendToAuctionOpen(false)}
                context="important"
            >
                <CRMPadding size="medium">
                    <CRMTitleForCard>Send to auction</CRMTitleForCard>
                    <CRMSpacer size="tiny" />
                    <CRMParagraph>
                        This ends the on-market flow but keeps conveyancing open. Marketing jobs will be removed.
                    </CRMParagraph>
                    <CRMSpacer size="medium" />
                    <CRMInputLabelAndErrorWrapComponent label="Auction routing date">
                        <input
                            id="went-to-auction-date"
                            type="date"
                            value={auctionDate}
                            onChange={(e) => setAuctionDate(e.target.value)}
                        />
                    </CRMInputLabelAndErrorWrapComponent>
                    <CRMSpacer size="large" />
                    <CRMSpacingRow justifyContent="end">
                        <CRMButtonSecondary label="Cancel" onClick={() => setSendToAuctionOpen(false)} />
                        <CRMButtonPrimary label="Confirm" onClick={submitSendToAuction} />
                    </CRMSpacingRow>
                </CRMPadding>
            </CRMCardOutsidePopupBlank>

            <CRMCardOutsidePopupBlank
                isOpen={reinstateOpen}
                onClose={() => setReinstateOpen(false)}
                context="important"
            >
                <CRMPadding size="medium">
                    <CRMTitleForCard>Reinstate listing</CRMTitleForCard>
                    <CRMSpacer size="tiny" />
                    <CRMParagraph>
                        This returns the listing to its previous status. If it was on the market when it was lost, you'll get a job to re-publish it to the portals.
                    </CRMParagraph>
                    <CRMSpacer size="large" />
                    <CRMSpacingRow justifyContent="end">
                        <CRMButtonSecondary label="Cancel" onClick={() => setReinstateOpen(false)} />
                        <CRMButtonPrimary label="Reinstate" onClick={submitReinstate} />
                    </CRMSpacingRow>
                </CRMPadding>
            </CRMCardOutsidePopupBlank>
        </div>
    );
};
